import type { GridPoint, LevelDefinition, LevelId, PowerUpKind } from '../types';

export const GRID_COLUMNS = 25;
export const GRID_ROWS = 13;

export const LEVELS: Record<LevelId, LevelDefinition> = {
  quick: {
    id: 'quick',
    name: 'Quick Play',
    eyebrow: 'The complete network run',
    lesson: 'A message follows connected systems to reach a server.',
    objective: 'Connect device → router → switch → internet → firewall → server.',
    duration: 60,
    hazards: 5,
    checkpoints: [
      { id: 'device', label: 'Home Device', shortLabel: 'DEVICE', x: 1, y: 10 },
      { id: 'router', label: 'Router Junction', shortLabel: 'ROUTER', x: 5, y: 6 },
      { id: 'switch', label: 'Network Switch', shortLabel: 'SWITCH', x: 9, y: 6 },
      { id: 'internet', label: 'Internet Highway', shortLabel: 'INTERNET', x: 13, y: 6 },
      { id: 'firewall', label: 'Firewall Gate', shortLabel: 'FIREWALL', x: 21, y: 6 },
      { id: 'server', label: 'TurnCom360 Server', shortLabel: 'SERVER', x: 23, y: 2 },
    ],
  },
  'level-1': {
    id: 'level-1',
    name: 'Level 1 — Home Connection',
    eyebrow: 'Device + Wi-Fi',
    lesson: 'Your device connects to a router using Wi-Fi or a cable.',
    objective: 'Travel from the home device to the router.',
    duration: 60,
    hazards: 0,
    checkpoints: [
      { id: 'device', label: 'Home Device', shortLabel: 'DEVICE', x: 1, y: 10 },
      { id: 'router', label: 'Home Router', shortLabel: 'ROUTER', x: 9, y: 6 },
    ],
  },
  'level-2': {
    id: 'level-2',
    name: 'Level 2 — Build the Network',
    eyebrow: 'Router + switch + cable',
    lesson: 'The switch helps several devices communicate.',
    objective: 'Collect the router, switch, and cable symbols in order.',
    duration: 75,
    hazards: 2,
    checkpoints: [
      { id: 'device', label: 'Home Device', shortLabel: 'START', x: 1, y: 10 },
      { id: 'router', label: 'Router Junction', shortLabel: 'ROUTER', x: 5, y: 6 },
      { id: 'switch', label: 'Network Switch', shortLabel: 'SWITCH', x: 9, y: 6 },
      { id: 'cable', label: 'Ethernet Link', shortLabel: 'CABLE', x: 17, y: 2 },
    ],
  },
  'level-3': {
    id: 'level-3',
    name: 'Level 3 — Deliver the Data',
    eyebrow: 'Internet + security + server',
    lesson: 'Data crosses the internet and reaches a server.',
    objective: 'Complete the full route while avoiding playful malware.',
    duration: 90,
    hazards: 5,
    checkpoints: [
      { id: 'device', label: 'Home Device', shortLabel: 'DEVICE', x: 1, y: 10 },
      { id: 'router', label: 'Router Junction', shortLabel: 'ROUTER', x: 5, y: 6 },
      { id: 'switch', label: 'Network Switch', shortLabel: 'SWITCH', x: 9, y: 6 },
      { id: 'internet', label: 'Internet Highway', shortLabel: 'INTERNET', x: 13, y: 6 },
      { id: 'firewall', label: 'Firewall Gate', shortLabel: 'FIREWALL', x: 21, y: 6 },
      { id: 'server', label: 'TurnCom360 Server', shortLabel: 'SERVER', x: 23, y: 2 },
    ],
  },
};

export const POWER_UP_POSITIONS: { kind: PowerUpKind; point: GridPoint }[] = [
  { kind: 'firewall-shield', point: { x: 3, y: 2 } },
  { kind: 'bandwidth-boost', point: { x: 7, y: 8 } },
  { kind: 'wifi-extender', point: { x: 11, y: 4 } },
  { kind: 'tech-support', point: { x: 15, y: 10 } },
  { kind: 'encryption', point: { x: 19, y: 2 } },
];

export const HAZARD_STARTS: GridPoint[] = [
  { x: 3, y: 4 },
  { x: 11, y: 8 },
  { x: 17, y: 4 },
  { x: 23, y: 8 },
  { x: 19, y: 10 },
];

function carveHorizontal(paths: Set<string>, y: number, fromX: number, toX: number): void {
  for (let x = fromX; x <= toX; x += 1) paths.add(`${x},${y}`);
}

function carveVertical(paths: Set<string>, x: number, fromY: number, toY: number): void {
  for (let y = fromY; y <= toY; y += 1) paths.add(`${x},${y}`);
}

export function createNetworkPaths(includeShortcut = false): Set<string> {
  const paths = new Set<string>();
  [2, 6, 10].forEach((y) => carveHorizontal(paths, y, 1, 23));
  carveHorizontal(paths, 4, 3, 21);
  carveHorizontal(paths, 8, 1, 23);
  [1, 5, 9, 13, 17, 21, 23].forEach((x) => carveVertical(paths, x, 2, 10));
  [3, 7, 11, 15, 19].forEach((x) => carveVertical(paths, x, 4, 8));
  if (includeShortcut) carveVertical(paths, 15, 8, 10);
  return paths;
}

export function pointKey(point: GridPoint): string {
  return `${point.x},${point.y}`;
}
